import { useEffect, useState } from 'react';
import type { FormEvent, ReactElement } from 'react';
import { useNavigate, useParams } from 'react-router-dom';
import {
  ABSTIMMUNG_GRENZEN,
  AUSWAHLART_WERTE,
  AUSWAHLREGEL_WERTE,
  MIN_AUSWAHLMOEGLICHKEITEN,
  VORLAGEN,
  beschreibeAuswahlregel,
} from '@mcikm-vote/shared';
import type { AuswahlartWert, AuswahlregelWert, Vorlage } from '@mcikm-vote/shared';

import {
  useAbstimmung,
  useAbstimmungAnlegen,
  useAbstimmungBearbeiten,
} from '../../admin/abstimmungenApi';
import { Feld } from '../../components/Feld';
import { TextEditor } from '../../components/TextEditor';
import { Fehlermeldung } from '../../components/Fehlermeldung';
import { Karte } from '../../components/Karte';
import { Knopf } from '../../components/Knopf';

/** Zwei leere Zeilen als Ausgangspunkt — §11.3 verlangt mindestens zwei Möglichkeiten. */
const LEERE_OPTIONEN = ['', ''];

/**
 * Anlegen und Bearbeiten einer Abstimmung (§10.1, §11).
 *
 * Umgesetzt ist ausschließlich die Einfachauswahl (§11.3); die Mehrfachauswahl aus §11.4 folgt in
 * S4 mit ihrer vollständigen Regelmatrix. Der Server weist sie bis dahin ausdrücklich ab, statt
 * sie halb zu unterstützen.
 *
 * Die Feldlängen sind sichtbar, damit eine Ablehnung nicht überrascht. Verbindlich prüft der
 * Server (§25.2); diese Anzeige dient allein der Benutzerführung (§13.1 Schritt 2).
 */
export function AbstimmungFormularSeite(): ReactElement {
  // Zwei Routen führen hierher: `/abstimmungen/:id/bearbeiten` und
  // `/veranstaltungen/:veranstaltungId/abstimmungen/neu`. Getrennte Parameternamen halten
  // eindeutig, welche Kennung gemeint ist.
  const { id, veranstaltungId: ausPfad } = useParams<{ id: string; veranstaltungId: string }>();
  const bearbeitet = id !== undefined;
  const navigate = useNavigate();

  const bestehend = useAbstimmung(id ?? '');
  const veranstaltungId = ausPfad ?? bestehend.data?.veranstaltungId ?? '';

  const anlegen = useAbstimmungAnlegen(veranstaltungId);
  const bearbeiten = useAbstimmungBearbeiten(id ?? '');
  const vorgang = bearbeitet ? bearbeiten : anlegen;

  const [ueberschrift, setUeberschrift] = useState('');
  const [text, setText] = useState('');
  const [optionen, setOptionen] = useState<string[]>(LEERE_OPTIONEN);
  const [nein, setNein] = useState(false);
  const [enthaltung, setEnthaltung] = useState(false);
  const [auswahlart, setAuswahlart] = useState<AuswahlartWert>('einfach');
  const [auswahlregel, setAuswahlregel] = useState<AuswahlregelWert>('bis_zu');
  const [stimmenanzahl, setStimmenanzahl] = useState(2);

  useEffect(() => {
    if (!bearbeitet || bestehend.data === undefined) {
      return;
    }
    const daten = bestehend.data;
    setUeberschrift(daten.ueberschrift);
    setText(daten.text);
    setOptionen(
      daten.optionen.filter((option) => option.art === 'regulaer').map((option) => option.text),
    );
    setNein(daten.sonderoptionNein);
    setEnthaltung(daten.sonderoptionEnthaltung);
    setAuswahlart(daten.auswahlart);
    setAuswahlregel(daten.auswahlregel ?? 'bis_zu');
    setStimmenanzahl(daten.stimmenanzahl ?? 2);
  }, [bearbeitet, bestehend.data]);

  const gefuellte = optionen.map((eintrag) => eintrag.trim()).filter((eintrag) => eintrag !== '');
  const auswaehlbar = gefuellte.length + (nein ? 1 : 0) + (enthaltung ? 1 : 0);
  const mehrfach = auswahlart === 'mehrfach';

  // §11.4: Die Stimmenanzahl darf die Zahl regulärer Möglichkeiten nicht überschreiten — sonst
  // wäre „genau X" von vornherein unerfüllbar.
  const anzahlPasst = !mehrfach || (stimmenanzahl >= 1 && stimmenanzahl <= gefuellte.length);

  const gueltig =
    ueberschrift.trim() !== '' &&
    text.trim() !== '' &&
    auswaehlbar >= MIN_AUSWAHLMOEGLICHKEITEN &&
    anzahlPasst;

  /** Übernimmt eine Vorlage in das Formular (§10.5). Sie erzeugt nur einen bearbeitbaren Entwurf. */
  function uebernimmVorlage(vorlage: Vorlage): void {
    const { eingabe } = vorlage;
    setUeberschrift(eingabe.ueberschrift);
    setText(eingabe.text);
    setOptionen(eingabe.optionen.length > 0 ? [...eingabe.optionen] : LEERE_OPTIONEN);
    setNein(eingabe.sonderoptionNein ?? false);
    setEnthaltung(eingabe.sonderoptionEnthaltung ?? false);
    setAuswahlart(eingabe.auswahlart);
    setAuswahlregel(eingabe.auswahlregel ?? 'bis_zu');
    setStimmenanzahl(eingabe.stimmenanzahl ?? 2);
  }

  function absenden(ereignis: FormEvent<HTMLFormElement>): void {
    ereignis.preventDefault();
    vorgang.mutate(
      {
        ueberschrift: ueberschrift.trim(),
        text: text.trim(),
        auswahlart,
        ...(mehrfach ? { stimmenanzahl, auswahlregel } : {}),
        sonderoptionNein: nein,
        sonderoptionEnthaltung: enthaltung,
        optionen: gefuellte,
      },
      {
        onSuccess: (abstimmung) => {
          void navigate(`/verwaltung/abstimmungen/${abstimmung.id}`);
        },
      },
    );
  }

  if (bearbeitet && bestehend.isPending) {
    return <p className="text-text-gedaempft text-sm">Wird geladen …</p>;
  }

  return (
    <div className="flex max-w-2xl flex-col gap-4">
      <h1 className="text-xl font-semibold">
        {bearbeitet ? 'Abstimmung bearbeiten' : 'Neue Abstimmung'}
      </h1>

      {!bearbeitet && (
        <Karte className="flex flex-col gap-3">
          <div>
            <h2 className="text-base font-semibold">Vorlage</h2>
            <p className="text-text-gedaempft mt-1 text-sm">
              Eine Vorlage füllt das Formular vor. Alles bleibt danach frei änderbar.
            </p>
          </div>

          <div className="flex flex-wrap gap-2">
            {VORLAGEN.map((vorlage) => (
              <Knopf
                key={vorlage.name}
                art="sekundaer"
                title={vorlage.beschreibung}
                onClick={() => {
                  uebernimmVorlage(vorlage);
                }}
              >
                {vorlage.name}
              </Knopf>
            ))}
          </div>
        </Karte>
      )}

      <form onSubmit={absenden} className="flex flex-col gap-4">
        <Karte className="flex flex-col gap-4">
          <Feld
            name="ueberschrift"
            beschriftung="Überschrift"
            wert={ueberschrift}
            grenze={ABSTIMMUNG_GRENZEN.ueberschrift}
            pflicht
            onAendern={setUeberschrift}
          />

          <TextEditor
            name="text"
            beschriftung="Abstimmungstext"
            wert={text}
            grenze={ABSTIMMUNG_GRENZEN.text}
            onAendern={setText}
          />
        </Karte>

        <Karte className="flex flex-col gap-4">
          <div>
            <h2 className="text-base font-semibold">Auswahlmöglichkeiten</h2>
            <p className="text-text-gedaempft mt-1 text-sm">
              Der Teilnehmer wählt genau eine Möglichkeit. Die Reihenfolge ist fest und wird nie
              zufällig verändert. Höchstens {ABSTIMMUNG_GRENZEN.regulaereOptionen} reguläre
              Möglichkeiten.
            </p>
          </div>

          {optionen.map((wert, index) => (
            <div key={index} className="flex items-end gap-2">
              <div className="flex-1">
                <Feld
                  name={`option-${index}`}
                  beschriftung={`Möglichkeit ${index + 1}`}
                  wert={wert}
                  grenze={ABSTIMMUNG_GRENZEN.optionstext}
                  onAendern={(neu) => {
                    setOptionen((alt) => alt.map((eintrag, i) => (i === index ? neu : eintrag)));
                  }}
                />
              </div>
              <Knopf
                art="sekundaer"
                className="mb-6"
                disabled={optionen.length <= 1}
                onClick={() => {
                  setOptionen((alt) => alt.filter((_, i) => i !== index));
                }}
              >
                Entfernen
              </Knopf>
            </div>
          ))}

          <div>
            <Knopf
              art="sekundaer"
              disabled={optionen.length >= ABSTIMMUNG_GRENZEN.regulaereOptionen}
              onClick={() => {
                setOptionen((alt) => [...alt, '']);
              }}
            >
              Möglichkeit hinzufügen
            </Knopf>
          </div>
        </Karte>

        <Karte className="flex flex-col gap-3">
          <div>
            <h2 className="text-base font-semibold">Auswahlart</h2>
            <p className="text-text-gedaempft mt-1 text-sm">
              {beschreibeAuswahlregel(auswahlart, stimmenanzahl, auswahlregel)}
            </p>
          </div>

          <fieldset className="flex flex-wrap gap-4">
            <legend className="sr-only">Auswahlart</legend>
            {AUSWAHLART_WERTE.map((wert) => (
              <label key={wert} className="flex items-center gap-2 text-sm">
                <input
                  type="radio"
                  name="auswahlart"
                  checked={auswahlart === wert}
                  onChange={() => {
                    setAuswahlart(wert);
                  }}
                />
                {wert === 'einfach' ? 'Einfachauswahl' : 'Mehrfachauswahl'}
              </label>
            ))}
          </fieldset>

          {mehrfach && (
            <div className="flex flex-wrap items-end gap-4">
              <fieldset className="flex flex-wrap gap-4">
                <legend className="mb-1 text-sm font-medium">Regel</legend>
                {AUSWAHLREGEL_WERTE.map((wert) => (
                  <label key={wert} className="flex items-center gap-2 text-sm">
                    <input
                      type="radio"
                      name="auswahlregel"
                      checked={auswahlregel === wert}
                      onChange={() => {
                        setAuswahlregel(wert);
                      }}
                    />
                    {wert === 'bis_zu' ? 'Bis zu X Stimmen' : 'Genau X Stimmen'}
                  </label>
                ))}
              </fieldset>

              <div className="flex flex-col gap-1">
                <label htmlFor="stimmenanzahl" className="text-sm font-medium">
                  Stimmenanzahl X
                </label>
                <input
                  id="stimmenanzahl"
                  type="number"
                  min={1}
                  max={Math.max(1, gefuellte.length)}
                  value={stimmenanzahl}
                  onChange={(ereignis) => {
                    setStimmenanzahl(Number(ereignis.target.value));
                  }}
                  className="border-rand bg-hintergrund min-h-touchflaeche w-24 rounded-md border px-3"
                />
              </div>

              {!anzahlPasst && (
                <p className="text-warnung text-sm">
                  Die Stimmenanzahl muss zwischen 1 und der Zahl der regulären Möglichkeiten (
                  {gefuellte.length}) liegen.
                </p>
              )}
            </div>
          )}
        </Karte>

        <Karte className="flex flex-col gap-3">
          <div>
            <h2 className="text-base font-semibold">Sonderoptionen</h2>
            <p className="text-text-gedaempft mt-1 text-sm">
              Sie zählen nicht zur Grenze der regulären Möglichkeiten und stehen für Teilnehmer
              hinter diesen.
            </p>
          </div>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={nein}
              onChange={(ereignis) => {
                setNein(ereignis.target.checked);
              }}
            />
            Möglichkeit „Nein" anbieten
          </label>

          <label className="flex items-center gap-2 text-sm">
            <input
              type="checkbox"
              checked={enthaltung}
              onChange={(ereignis) => {
                setEnthaltung(ereignis.target.checked);
              }}
            />
            Möglichkeit „Enthaltung" anbieten
          </label>
        </Karte>

        {!gueltig && (
          <p className="text-text-gedaempft text-sm">
            Überschrift, Abstimmungstext und mindestens {MIN_AUSWAHLMOEGLICHKEITEN} auswählbare
            Möglichkeiten sind erforderlich. Derzeit: {auswaehlbar}.
          </p>
        )}

        {vorgang.isError && <Fehlermeldung>{vorgang.error.message}</Fehlermeldung>}

        <div className="flex flex-wrap gap-2">
          <Knopf type="submit" disabled={!gueltig || vorgang.isPending}>
            {vorgang.isPending ? 'Wird gespeichert …' : 'Speichern'}
          </Knopf>
          <Knopf
            art="sekundaer"
            onClick={() => {
              void navigate(-1);
            }}
          >
            Abbrechen
          </Knopf>
        </div>
      </form>
    </div>
  );
}
